<template>
    <div class="h-100">
        <grid
            ref="grid"
            :fetch-callback="fetchCallback"
            :column-defs="columnDefs"
            :is-auto-width="true"
            grid-theme="ag-theme-alpine-partner"
            :maxBlocksInCache="null"
        >
            <responsive-dropdown
                :icon="['fas', 'star']"
                :items="reviewTypeOptions"
                variant="primary"
            >
                {{ reviewTypeLabel }}
            </responsive-dropdown>
            <template v-if="reviewType === 'guest'">
                <date-range-picker
                    key="guest-reviews-date-range-picker"
                    :range-options="predefinedRanges"
                    :value="guestReviewsDateRange"
                    :date-format="userUnicodeDateFormat"
                    @input="guestReviewsDateRangeChange"
                />
                <responsive-dropdown
                    :icon="['fas', 'filter']"
                    :items="reviewResponseStatusFilterOptions"
                    variant="primary"
                >
                    {{ reviewResponseStatusFilterLabel }}
                </responsive-dropdown>
                <b-button-group class="d-none d-lg-block mr-1">
                    <search-multiselect
                        :icon="['fas', 'hotel']"
                        v-model="selectedProperties"
                        :options="propertiesOptions"
                        variant="primary"
                        itemLabel="propertie"
                        placeholder="Properties"
                        controlValue="Properties"
                        @input="refresh"
                    ></search-multiselect>
                </b-button-group>
                <b-button-group class="d-lg-none mr-1">
                    <more-filters-dropdown>
                        <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                            <b-button-group>
                                <search-multiselect
                                    :icon="['fas', 'hotel']"
                                    v-model="selectedProperties"
                                    :options="propertiesOptions"
                                    variant="primary"
                                    itemLabel="propertie"
                                    placeholder="Properties"
                                    controlValue="Properties"
                                    @input="refresh"
                                    @show="handleDropdownShow"
                                    @hide="handleDropdownHide"
                                ></search-multiselect>
                            </b-button-group>
                        </template>
                    </more-filters-dropdown>
                </b-button-group>
                <fa-icon-tooltip id="icon-info-guest" :icon="['fas', 'circle-info']" class="text-primary h-100" variant="info">
                    <p style="text-align: left">
                        The Airbnb host has 14 days to respond to a review left by a guest (stay review).
                        After 14 days, the submission window closes (expires).
                    </p>
                </fa-icon-tooltip>
            </template>
            <template v-else>
                <date-range-picker
                    key="host-reviews-date-range-picker"
                    :range-options="predefinedRanges"
                    :value="hostReviewsDateRange"
                    :date-format="userUnicodeDateFormat"
                    @input="hostReviewsDateRangeChange"
                />
                <responsive-dropdown
                    :icon="['fas', 'filter']"
                    :items="reviewStatusFilterOptions"
                    variant="primary"
                >
                    {{ reviewStatusFilterLabel }}
                </responsive-dropdown>
                <fa-icon-tooltip id="icon-info-guest" :icon="['fas', 'circle-info']" class="text-primary h-100" variant="info">
                    <p style="text-align: left">
                        Airbnb allows 14 days after a guest departure for a host to submit a guest review.
                        After 14 days, the submission window closes (expires).
                    </p>
                </fa-icon-tooltip>
            </template>
        </grid>
        <submit-guest-review-modal
            :busy="submitGuestReviewModalLoading"
            @hidden="submitGuestReviewModalHidden"
            :id="submitGuestReviewModalId"
            :is-view="isView"
            :key="submitGuestReviewModalId"
            :review="currentHostReview"
            @submit-guest-review="submitGuestReview"
            :tags="tags"
        />
    </div>
</template>

<script>
import actionsCellRenderer from './airbnb/host-reviews/actions-cell-renderer.vue';
import API from '../../../api.jsx';
import DateRangePicker, {
    ALL_DATES,
    LAST_MONTH,
    LAST_YEAR,
    LAST_WEEK,
    THIS_WEEK,
    nameToRange,
    THIS_MONTH,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
    FUTURE,
} from '../../generic/date-range-picker/date-range-picker.vue';
import { escapeHTML } from '../../../utils/strings';
import FaIconTooltip from '../../generic/fa-icon-tooltip/fa-icon-tooltip.vue';
import format from 'date-fns/format';
import { formatResponseError } from '../../../utils/api';
import Grid from '../../generic/grid/grid.vue';
import GuestReviewResponseCellRenderer from './airbnb/guest-reviews/response-cell-renderer.vue';
import GuestReviewCellRenderer from './airbnb/guest-reviews/review-cell-renderer.vue';
import HostReviewCellRenderer from './airbnb/host-reviews/public-review-cell-renderer.vue';
import HostReviewResponseCellRenderer from './airbnb/host-reviews/guest-response-cell-rederer.vue';
import { mapGetters, mapActions } from 'vuex';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import {
    AIRBNB_REVIEW_CATEGORY_TAG_TAGS_ENDPOINT,
    PARTNER_AIRBNB_HOST_REVIEW_GRID_ENDPOINT,
    PARTNER_AIRBNB_HOST_REVIEW_PROPERTIES_ENDPOINT,
    PARTNER_AIRBNB_GUEST_REVIEW_GRID_ENDPOINT,
    PARTNER_AIRBNB_GUEST_REVIEW_SUBMIT_AIRBNB_GUEST_REVIEW_ENDPOINT,
} from '../../../endpoints';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
import {
    REVIEW_STATUS_PENDING,
    REVIEW_STATUS_EXPIRES_SOON,
    REVIEW_STATUS_SUBMITTED,
    REVIEW_STATUS_EXPIRED,
} from '../../../constants';
import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
import StarsCellRenderer from './airbnb/guest-reviews/stars-cell-renderer.vue';
import StatusCellRenderer from './airbnb/host-reviews/status-cell-renderer.vue';
import SubmitGuestReviewModal from '../../common/submit-guest-review-modal.vue';

export default {
    components: {
        FaIconTooltip,
        DateRangePicker,
        Grid,
        MoreFiltersDropdown,
        ResponsiveButton,
        ResponsiveDropdown,
        SearchMultiselect,
        SubmitGuestReviewModal,
        guestReviewResponseCellRenderer: GuestReviewResponseCellRenderer,
        guestReviewCellRenderer: GuestReviewCellRenderer,
        hostReviewCellRenderer: HostReviewCellRenderer,
        hostReviewResponseCellRenderer: HostReviewResponseCellRenderer,
        starsCellRenderer: StarsCellRenderer,
        statusCellRenderer: StatusCellRenderer,
    },
    data() {
        return {
            dateRequestFormat: 'yyyy-MM-dd',
            guestReviewsDateRange: nameToRange(THIS_YEAR),
            hostReviewsDateRange: nameToRange(THIS_YEAR),
            selectedProperties: [],
            reviewTypeLabel: null,
            reviewResponseStatusFilter: 'all',
            reviewResponseStatusFilterLabel: 'Show All Reviews',
            reviewStatusFilter: '',
            reviewStatusFilterLabel: 'Show All Reviews',
            properties: [],
            currentHostReview: null,
            currentHostReviewNode: null,
            submitGuestReviewModalId: null,
            submitGuestReviewModalLoading: false,
            predefinedRanges: [
                FUTURE,
                TODAY,
                YESTERDAY,
                THIS_WEEK,
                LAST_WEEK,
                THIS_MONTH,
                LAST_MONTH,
                THIS_YEAR,
                LAST_YEAR,
                ALL_DATES,
            ],
            tags: {},
            isTagsUploaded: false,
        };
    },
    created() {
        API.get(PARTNER_AIRBNB_HOST_REVIEW_PROPERTIES_ENDPOINT())
            .then(({ data }) => {
                this.properties = data;
            })
            .catch(() => {
                this.errorToast({
                    text: 'Could not load properties.',
                });
            });
        if (this.reviewType === 'guest') {
            this.reviewTypeLabel = 'Stay Reviews (by Guest)';
        } else if (this.reviewType === 'host') {
            this.reviewTypeLabel = 'Guest Reviews (by Host)';
        } else {
            this.changeReviewType('host');
            this.reviewTypeLabel = 'Guest Reviews (by Host)';
        }
    },
    methods: {
        ...mapActions('partnerAirbnbReviewsLocalStorage', ['changeReviewType']),
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        guestReviewsDateRangeChange(value) {
            this.guestReviewsDateRange = { ...value };
            this.refresh();
        },
        hostReviewsDateRangeChange(value) {
            this.hostReviewsDateRange = { ...value };
            this.refresh();
        },
        async showSubmitGuestReviewModal(review, node) {
            this.currentHostReview = review;
            this.currentHostReviewNode = node;
            if (this.isView) {
                this.submitGuestReviewModalId = `submit-guest-review-modal-id-${review.id}`;
                this.$nextTick(() => {
                    this.$bvModal.show(this.submitGuestReviewModalId);
                });
            } else {
                if (this.isTagsUploaded) {
                    this.submitGuestReviewModalId = `submit-guest-review-modal-id-${review.id}`;
                    this.$nextTick(() => {
                        this.$bvModal.show(this.submitGuestReviewModalId);
                    });
                } else {
                    this.$parent.$emit('busy', true);
                    try {
                        const { data } = await API.get(AIRBNB_REVIEW_CATEGORY_TAG_TAGS_ENDPOINT());
                        this.tags = data;
                        this.isTagsUploaded = true;
                        this.submitGuestReviewModalId = `submit-guest-review-modal-id-${review.id}`;
                        this.$nextTick(() => {
                            this.$bvModal.show(this.submitGuestReviewModalId);
                        });
                    } catch (error) {
                        const { generic } = formatResponseError(error);
                        this.errorToast({
                            text: generic || 'Could not load tags.',
                        });
                    } finally {
                        this.$parent.$emit('busy', false);
                    }
                }
            }
        },
        async submitGuestReview(reviewData) {
            this.submitGuestReviewModalLoading = true;
            try {
                const { data } = await API.post(
                    PARTNER_AIRBNB_GUEST_REVIEW_SUBMIT_AIRBNB_GUEST_REVIEW_ENDPOINT(this.currentHostReview.id),
                    {
                        public_review: reviewData.publicReview,
                        private_feedback: reviewData.privateFeedback,
                        cleanliness: reviewData.cleanliness,
                        communication: reviewData.communication,
                        respect_house_rules: reviewData.respectHouseRules,
                        is_reviewee_recommended: reviewData.isRevieweeRecommended,
                        tags: reviewData.tags,
                    }
                );
                this.currentHostReviewNode.setData(data);
                this.$bvModal.hide(this.submitGuestReviewModalId);
                this.successToast({
                    text: 'Guest review successfully submitted.',
                });
            } catch (error) {
                const { generic } = formatResponseError(error);
                this.errorToast({
                    text: generic || 'Could not submit guest review.',
                });
            } finally {
                this.submitGuestReviewModalLoading = false;
            }
        },
        submitGuestReviewModalHidden() {
            this.currentHostReview = null;
            this.currentHostReviewNode = null;
            this.submitGuestReviewModalId = null;
        },
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        ...mapGetters('partnerAirbnbReviewsLocalStorage', ['reviewType']),
        columnDefs() {
            if (this.reviewType === 'guest') {
                return [
                    {
                        field: 'rental_property.id',
                        headerName: 'P. ID',
                        minWidth: 70,
                    },
                    {
                        field: 'date',
                        headerName: 'Date',
                        type: 'dateColumn',
                        sort: 'desc',
                        minWidth: 150,
                        maxWidth: 160,
                    },
                    {
                        field: 'meta_reservation.id',
                        headerName: 'Inv. ID',
                        minWidth: 100,
                        cellRenderer: ({ data }) => {
                            return data.meta_reservation
                                ? `<a style="text-decoration: underline;" href="/app/reservation/edit/invoice/${data.meta_reservation.id}">${data.meta_reservation.id}</a>`
                                : '';
                        },
                    },
                    {
                        field: 'rental_property.name',
                        headerName: 'Property',
                        minWidth: 200,
                    },
                    {
                        field: 'meta_reservation.guest_name',
                        headerName: 'Guest Name',
                        minWidth: 160,
                    },
                    {
                        field: 'stars',
                        headerName: 'Stars',
                        minWidth: 140,
                        maxWidth: 140,
                        cellRenderer: 'starsCellRenderer',
                    },
                    {
                        field: 'description',
                        headerName: 'Review',
                        minWidth: 200,
                        autoWidthExclude: true,
                        cellRenderer: 'guestReviewCellRenderer',
                    },
                    {
                        field: 'airbnb_host_review_response.response',
                        headerName: 'Response',
                        minWidth: 200,
                        autoWidthExclude: true,
                        autoHeight: true,
                        cellRenderer: 'guestReviewResponseCellRenderer',
                        cellRendererParams: ({ node }) => {
                            return {
                                setReview: (review) => {
                                    node.setData(review);
                                },
                            };
                        },
                    },
                ];
            } else {
                return [
                    {
                        field: 'review_status',
                        headerName: 'Status',
                        minWidth: 100,
                        sort: 'asc',
                        cellRenderer: 'statusCellRenderer',
                    },
                    {
                        field: 'expires_at',
                        headerName: 'Expiration Date',
                        type: 'dateTimeColumn',
                        minWidth: 150,
                        maxWidth: 160,
                    },
                    {
                        field: 'meta_res.id',
                        headerName: 'Inv. ID',
                        minWidth: 100,
                        cellRenderer: ({ data }) => {
                            return `<a style="text-decoration: underline;" href="/app/reservation/edit/invoice/${data.meta_res.id}">${data.meta_res.id}</a>`;
                        },
                    },
                    {
                        field: 'meta_res.property_names',
                        headerName: 'Property',
                        cellRenderer: ({ data }) => {
                            return data.meta_res.property_names
                                .map((name) => {
                                    return escapeHTML(name);
                                })
                                .join('<br />');
                        },
                        sortable: false,
                        autoHeight: true,
                        minWidth: 200,
                    },
                    {
                        field: 'meta_res.guest_name',
                        headerName: 'Guest Name',
                        minWidth: 160,
                    },
                    {
                        field: 'public_review',
                        headerName: 'Public Review',
                        minWidth: 200,
                        autoWidthExclude: true,
                        cellRenderer: 'hostReviewCellRenderer',
                        cellRendererParams: ({ data, node }) => {
                            return {
                                showSubmitGuestReviewModal: () => {
                                    this.showSubmitGuestReviewModal(data, node);
                                },
                            };
                        },
                    },
                    {
                        field: 'guest_review_response.response',
                        headerName: 'Guest Response',
                        minWidth: 200,
                        autoWidthExclude: true,
                        cellRenderer: 'hostReviewResponseCellRenderer',
                    },
                    {
                        headerName: '',
                        maxWidth: 60,
                        minWidth: 60,
                        resizable: false,
                        sortable: false,
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: ({ data, node }) => {
                            return {
                                showSubmitGuestReviewModal: () => {
                                    this.showSubmitGuestReviewModal(data, node);
                                },
                            };
                        },
                    },
                ];
            }
        },
        isView() {
            return this.currentHostReview?.review_status === REVIEW_STATUS_SUBMITTED;
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                if (this.reviewType === 'guest') {
                    return API.get(PARTNER_AIRBNB_HOST_REVIEW_GRID_ENDPOINT(), {
                        params: {
                            offset: offset,
                            limit: limit,
                            sort: sort,
                            desc: desc,
                            search: search,
                            start_date: format(this.guestReviewsDateRange.from, this.dateRequestFormat),
                            end_date: format(this.guestReviewsDateRange.to, this.dateRequestFormat),
                            review_response_status_filter: this.reviewResponseStatusFilter,
                            property_ids: this.selectedProperties.join(','),
                        },
                    });
                } else {
                    return API.get(PARTNER_AIRBNB_GUEST_REVIEW_GRID_ENDPOINT(), {
                        params: {
                            offset: offset,
                            limit: limit,
                            sort: sort,
                            desc: desc,
                            search: search,
                            start_date: format(this.hostReviewsDateRange.from, this.dateRequestFormat),
                            end_date: format(this.hostReviewsDateRange.to, this.dateRequestFormat),
                            review_status_filter: this.reviewStatusFilter,
                        },
                    });
                }
            };
        },
        reviewTypeOptions() {
            return [
                {
                    label: 'Stay Reviews (by Guest)',
                    click: async () => {
                        await this.changeReviewType('guest');
                        this.reviewTypeLabel = 'Stay Reviews (by Guest)';
                    },
                },
                {
                    label: 'Guest Reviews (by Host)',
                    click: async () => {
                        await this.changeReviewType('host');
                        this.reviewTypeLabel = 'Guest Reviews (by Host)';
                    },
                },
            ];
        },
        reviewResponseStatusFilterOptions() {
            return [
                {
                    label: 'Show All Reviews',
                    click: () => {
                        this.reviewResponseStatusFilter = 'all';
                        this.reviewResponseStatusFilterLabel = 'Show All Reviews';
                        this.refresh();
                    },
                },
                {
                    label: 'Show Only Reviews with Pending Responses',
                    click: () => {
                        this.reviewResponseStatusFilter = 'pending';
                        this.reviewResponseStatusFilterLabel = 'Show Only Reviews with Pending Responses';
                        this.refresh();
                    },
                },
            ];
        },
        reviewStatusFilterOptions() {
            return [
                {
                    label: 'Show All Reviews',
                    click: () => {
                        this.reviewStatusFilter = '';
                        this.reviewStatusFilterLabel = 'Show All Reviews';
                        this.refresh();
                    },
                },
                {
                    label: 'Show Pending Reviews',
                    click: () => {
                        this.reviewStatusFilter = REVIEW_STATUS_PENDING;
                        this.reviewStatusFilterLabel = 'Show Pending Reviews';
                        this.refresh();
                    },
                },
                {
                    label: 'Show Expires Soon Reviews',
                    click: () => {
                        this.reviewStatusFilter = REVIEW_STATUS_EXPIRES_SOON;
                        this.reviewStatusFilterLabel = 'Show Expires Soon Reviews';
                        this.refresh();
                    },
                },
                {
                    label: 'Show Submitted Reviews',
                    click: () => {
                        this.reviewStatusFilter = REVIEW_STATUS_SUBMITTED;
                        this.reviewStatusFilterLabel = 'Show Submitted Reviews';
                        this.refresh();
                    },
                },
                {
                    label: 'Show Expired Reviews',
                    click: () => {
                        this.reviewStatusFilter = REVIEW_STATUS_EXPIRED;
                        this.reviewStatusFilterLabel = 'Show Expired Reviews';
                        this.refresh();
                    },
                },
            ];
        },
        propertiesOptions() {
            return this.properties.map((property) => {
                return {
                    text: property.name,
                    value: property.property_id,
                };
            });
        },
    },
};
</script>
